import PersonalDetails from "./steps/personalDetails";
import SystemCheck from "./steps/systemCheck";
import TermsAndConditions from "./steps/termsAndConditions";
import { useTranslation } from "react-i18next";

import { cn } from "../../../../../utils/cn";
import { useAppDispatch, useAppSelector } from "../../../../../store/store";
import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import AudioVideoCheck from "./steps/audioCheck";

interface StepperFormProps {
  isResume?: boolean;
}

const StepperForm = ({ isResume = false }: StepperFormProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const stepperFormActiveStep = useAppSelector(
    (state) => state.AssessmentSlice.preAssessment.stepperFormActiveStep
  );

  const tncEnabled = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.tncEnabled
  );

  const tncData = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.tncData
  );

  const videoProctoring = useAppSelector(
    (state) => state.AssessmentSlice.preAssessmentData?.videoProctoring
  );

  const isSubjective = useAppSelector(
    (state) =>
      state.AssessmentSlice.preAssessmentData?.questionTypeCountMap?.subjective
  );

  const handleStepChange = (newStep: "next" | "previous") => {
    const curr = stepperFormActiveStep;
    if (newStep === "next") {
      if (curr === 1) {
        if (isSubjective > 0)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(2));
        else if (videoProctoring)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(3));
        else if (tncEnabled)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(4));
      } else if (curr === 2) {
        if (videoProctoring)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(3));
        else dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(4));
      } else {
        dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(4));
      }
    } else {
      if (curr === 4) {
        if (videoProctoring)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(3));
        else if (isSubjective > 0)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(2));
        else dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(1));
      } else if (curr === 3) {
        if (isSubjective > 0)
          dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(2));
        else dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(1));
      } else {
        dispatch(AssessmentSliceActions.mutateStepperFormActiveStep(1));
      }
    }
  };

  const STEPS = {
    1: {
      title: t("assessment.stepper.personalDetails"),
      component: <PersonalDetails setActiveStep={handleStepChange} />
    },

    ...(isSubjective > 0 && {
      2: {
        title: t("assessment.stepper.mediaCheck"),
        component: <AudioVideoCheck setActiveStep={handleStepChange} />
      }
    }),
    ...(videoProctoring && {
      3: {
        title: t("assessment.stepper.systemCheck"),
        component: (
          <SystemCheck
            inResumeScreen={isResume}
            setActiveStep={handleStepChange}
          />
        )
      }
    }),
    ...(tncEnabled && {
      4: {
        title: t("assessment.stepper.termsAndConditions"),
        component: (
          <TermsAndConditions
            statement={
              tncData?.tncMap?.["en_IN"] ||
              t("assessment.stepper.defaultTncStatement")
            }
            setActiveStep={handleStepChange}
          />
        )
      }
    })
  };

  // TODO: Add start assessment handler here

  return (
    <div className="flex flex-col w-full flex-1 px-4 sm:px-8 md:px-10 lg:px-16 pb-6 pt-12 lg:pt-16 border-t-2 lg:border-t-0 overflow-y-auto thin-scrollbar">
      {/* Stepper  */}
      <div className="flex flex-col sm:flex-row items-start w-full gap-6">
        {Object.keys(STEPS).map((step: any, index: number) => (
          <div key={step} className="flex-1">
            <div
              className={cn(
                "bg-secondary/20 w-full h-1 rounded-3xl",
                stepperFormActiveStep === Number(step) && "bg-primary/50",
                stepperFormActiveStep > Number(step) && "bg-primary"
              )}
            />
            <div className="mt-4">
              <h3
                className={cn(
                  "font-medium text-secondary/70 text-sm md:text-base",
                  stepperFormActiveStep >= Number(step) && "text-primary"
                )}
              >
                {t("assessment.stepper.stepLabel", {
                  step: index + 1
                })}
              </h3>
              <p
                className={cn(
                  "font-medium text-secondary/70 text-sm md:text-base",
                  stepperFormActiveStep === Number(step) && "text-primary"
                )}
              >
                {STEPS[step as keyof typeof STEPS]?.title}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 sm:mt-10">
        {STEPS[stepperFormActiveStep as keyof typeof STEPS]?.component}
      </div>
    </div>
  );
};

export default StepperForm;
